
import { computed, defineComponent, h, onMounted, PropType, ref } from 'vue-demi';
import { Plus as PlusIcon } from '@vicons/fa';
import { HoundModel, formatNumberDecimals, emptyAddress } from '../../../common';
import { EthersService as eth, convertToUSD } from '../services';
import { FormInst, FormItemRule } from 'naive-ui';
import { notification, toast } from '../utils/notification.util';
import BaseNotificationContent from './shared/BaseNotificationContent.vue';
import { BigNumber } from 'ethers';
import { exchangeRatesAvailable, exchangeRates, staminaRefillCurrency, staminaRefill1x, staminaCap, staminaPerTimeUnit } from '../data/global';
import { default as dayjs } from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { Bolt as StaminaIcon } from '@vicons/fa';

dayjs.extend(utc);

export default defineComponent({
  emits: ['refillStamina', 'cancel'],
  name: 'StaminaRefillModal',
  components: { PlusIcon, StaminaIcon },
  props: {
    hound: {
      type: Object as PropType<HoundModel>,
      default: {},
      required: true
    }
  },
  setup (props, { emit }) {
    const formRef = ref<FormInst | null>(null);
    const formValue = ref({
      unitsToRefill: null
    });
    const hound = props.hound as HoundModel;
    const loading = ref(false);
    const replenishLoading = ref(false);
    const refreshedStamina = Math.min(
      Math.floor(dayjs.utc().diff(dayjs.utc(hound.staminaLastUpdate), 'seconds')/staminaPerTimeUnit.value),
      staminaCap.value - hound.stamina
    );

    const actualUnitsUntilFull = staminaCap.value - hound.stamina - refreshedStamina;

    const cancel = () => {
      emit('cancel');
    }

    const refillFee = computed(() => {
      if (formValue.value.unitsToRefill)
        return BigNumber.from(staminaRefill1x.value).mul(formValue.value.unitsToRefill);
      return null
    });

    const customCurrency = computed(() => staminaRefillCurrency.value !== emptyAddress);

    const currencyName = customCurrency.value
      ? eth.currenciesInfo.find(c => c.value === staminaRefillCurrency.value)?.label
      : eth.reactive.value.network.nativeCurrency.symbol;

    const replenishStamina = async () => {
      try {
        replenishLoading.value = true;
        const tx = await eth.hounds.boostHoundStamina(
          hound.token_id,
          eth.reactive.value.userAddress,
          eth.utils.parseEther('0'),
          {
            value: eth.utils.parseEther('0'),
            gasLimit: 3000000
          }
        )
        await tx.wait();
        
        notification.create({
          title: 'Stamina Replenished',
          description: `You've successfully replenished the stamina of ${hound.name}'s.`,
          content: () => h(
            BaseNotificationContent,
            {
              infoElements: [
                { label: 'Hound', content: hound.name },
                { label: 'Stamina replenished', content: refreshedStamina.toString() }
              ]
            }
          ),
          meta: new Date().toUTCString()
        });
        
        emit('refillStamina');
        toast.success('Successfully refilled stamina');
      } catch(err) {
        toast.error('Transaction Failed');
        console.log(err);
      } finally {
        replenishLoading.value = false;
        cancel();
      }
    }
    const refillStamina = () => {
      formRef.value?.validate(async (errors) => {
        if (errors) {
          toast.error('Invalid values');
          return;
        }
        try {
          loading.value = true;
          // standard token: value should reflect the amount, third argument (payed) should be 0
          // non-standard token: value should be 0, third argument (payed) should reflect the amount
          if (staminaRefillCurrency.value !== emptyAddress) {
            const customTokenInterface = eth.getCustomTokenInterface(staminaRefillCurrency.value);
            const alreadyApproved = await customTokenInterface.allowance(eth.userAddress, eth.contractAddresses.PAYMENTS);
            if (alreadyApproved.lte(refillFee.value)) {
              const approveTx = await eth.getCustomTokenInterface(
                staminaRefillCurrency.value
              ).approve(
                eth.contractAddresses.PAYMENTS, 
                BigNumber.from("1000000000000000000").gt(refillFee.value) ? "1000000000000000000" : refillFee.value
              );
              await approveTx.wait();
            }
          }
          const tx = await eth.hounds.boostHoundStamina(
            hound.token_id,
            eth.reactive.value.userAddress,
            customCurrency.value ? refillFee.value : eth.utils.parseEther('0'),
            {
              value: customCurrency.value ? eth.utils.parseEther('0') : refillFee.value,
              gasLimit: 3000000
            }
          )
          await tx.wait();
          
          notification.create({
            title: 'Stamina Boosted',
            description: `You've successfully boosted the stamina of ${hound.name}'s.`,
            content: () => h(
              BaseNotificationContent,
              {
                infoElements: [
                  { label: 'Hound', content: hound.name },
                  { label: 'Refill Fee', content: `${eth.utils.formatEther(refillFee.value)} ${currencyName}` }
                ]
              }
            ),
            meta: new Date().toUTCString()
          });
          
          emit('refillStamina');
          toast.success('Successfully refilled stamina');
        } catch(err) {
          toast.error('Transaction Failed');
          console.log(err);
        } finally {
          loading.value = false;
          cancel();
        }
      })
    };

    return {
      formRef,
      eth,
      hound,
      actualUnitsUntilFull,
      loading,
      replenishLoading,
      cancel,
      refillStamina,
      replenishStamina,
      formValue,
      formatNumberDecimals,
      currencyName,
      rules: {
        unitsToRefill: {
          validator: (rule: FormItemRule, value: any) => {
            return new Promise<void>((resolve, reject) => {
              if (value === null) reject(Error('Refill amount is required'));
              if (typeof value !== 'number') reject(Error('Refill amount must be a number'));
              if (!(value >= 0.1))  reject(Error('Refill amount must be greater than 0.1'));
              if (!(value <= actualUnitsUntilFull))  reject(Error('Refill amount too big'));
              resolve();
            })
          }
        }
      },
      refillFee,
      convertToUSD,
      exchangeRatesAvailable,
      exchangeRates,
      staminaRefillCurrency,
      staminaRefill1x,
      staminaCap,
      refreshedStamina
    }
  }
});
